import type {
  Installation,
  PortfolioMetrics,
  RevenueEntry,
  SeedEventRow,
  Subscription,
} from "./types";

function inWindow(value: string | null, from: Date, to: Date): boolean {
  if (!value) return false;
  const time = new Date(value).getTime();
  return time >= from.getTime() && time <= to.getTime();
}

function happenedBy(value: string | null, to: Date): boolean {
  return Boolean(value && new Date(value).getTime() <= to.getTime());
}

function rate(numerator: number, denominator: number): number | null {
  return denominator > 0 ? numerator / denominator : null;
}

function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  const value = sorted[middle];
  if (value === undefined) return null;
  if (sorted.length % 2 === 1) return value;
  const previous = sorted[middle - 1];
  return previous === undefined ? value : (previous + value) / 2;
}

function installationKey(row: Pick<Installation, "deploymentId" | "tenantId">): string {
  return `${row.deploymentId}:${row.tenantId}`;
}

function subscriptionKey(row: Pick<Subscription, "deploymentId" | "tenantId">): string {
  return `${row.deploymentId}:${row.tenantId}`;
}

export function subscriptionContributesMrrAt(row: Subscription, at: Date): boolean {
  const atTime = at.getTime();
  if (new Date(row.startedAt).getTime() > atTime) return false;
  if (row.canceledAt) {
    if (new Date(row.canceledAt).getTime() <= atTime) return false;
    return row.mrrCents > 0;
  }
  return row.status === "active" && row.mrrCents > 0;
}

export function calculatePortfolioMetrics(input: {
  installations: Installation[];
  subscriptions: Subscription[];
  revenue: RevenueEntry[];
  events: SeedEventRow[];
  from: Date;
  to: Date;
}): PortfolioMetrics {
  const { installations, subscriptions, revenue, from, to } = input;

  // Conversion rates are cohort-based. A tenant installed before `from` must not
  // inflate activation/first-value conversion for installs acquired in this window.
  const installCohort = installations.filter((row) => inWindow(row.installedAt, from, to));
  const cohortKeys = new Set(installCohort.map(installationKey));
  const activatedCohort = installCohort.filter((row) => happenedBy(row.activatedAt, to));
  const firstValueCohort = installCohort.filter((row) => happenedBy(row.firstValueAt, to));
  const trialCohort = installCohort.filter((row) => happenedBy(row.trialStartedAt, to));
  const paidCohortSubscriptions = subscriptions.filter(
    (row) =>
      cohortKeys.has(subscriptionKey(row)) && new Date(row.startedAt).getTime() <= to.getTime(),
  );
  const trialKeys = new Set(trialCohort.map(installationKey));
  const paidTrialSubscriptions = paidCohortSubscriptions.filter((row) =>
    trialKeys.has(subscriptionKey(row)),
  );

  const installs = installCohort.length;
  const activeInstallations = installations.filter((row) => {
    const installed = new Date(row.installedAt).getTime() <= to.getTime();
    const notGone = !row.uninstalledAt || new Date(row.uninstalledAt).getTime() > to.getTime();
    return installed && notGone;
  }).length;
  const activations = activatedCohort.length;
  const firstValues = firstValueCohort.length;
  const trialsStarted = trialCohort.length;

  const activeSubscriptions = subscriptions.filter((row) => subscriptionContributesMrrAt(row, to));
  const paidCustomers = activeSubscriptions.length;
  const mrrCents = activeSubscriptions.reduce((sum, row) => sum + row.mrrCents, 0);
  const revenueCents = revenue
    .filter((row) => inWindow(row.recognizedAt, from, to))
    .reduce((sum, row) => sum + row.amountCents, 0);

  const churnedCustomers = subscriptions.filter((row) => inWindow(row.canceledAt, from, to)).length;
  const paidAtWindowStart = subscriptions.filter((row) =>
    subscriptionContributesMrrAt(row, from),
  ).length;

  const ttfvMinutes = firstValueCohort.flatMap((row) => {
    if (!row.firstValueAt) return [];
    const diff = new Date(row.firstValueAt).getTime() - new Date(row.installedAt).getTime();
    return diff >= 0 ? [diff / 60_000] : [];
  });

  return {
    installs,
    activeInstallations,
    activations,
    activationRate: rate(activations, installs),
    firstValues,
    firstValueRate: rate(firstValues, activations),
    trialsStarted,
    paidCustomers,
    paidFromTrialRate: rate(paidTrialSubscriptions.length, trialCohort.length),
    paidFromInstallRate: rate(paidCohortSubscriptions.length, installs),
    mrrCents,
    arrCents: mrrCents * 12,
    revenueCents,
    arpuCents: paidCustomers > 0 ? Math.round(mrrCents / paidCustomers) : null,
    churnedCustomers,
    logoChurnRate: rate(churnedCustomers, paidAtWindowStart),
    medianTtfvMinutes: median(ttfvMinutes),
  };
}

export const diagnosticMap = [
  { from: "impressions", to: "listing_views", area: "nome/posição/categoria" },
  { from: "listing_views", to: "installs", area: "dor/copy/preço/confiança" },
  { from: "installs", to: "activations", area: "onboarding" },
  { from: "activations", to: "first_value", area: "produto/UX" },
  { from: "first_value", to: "paid", area: "preço/valor/recorrência" },
  { from: "paid", to: "retention_30d", area: "retenção/fit" },
  { from: "retention_30d", to: "retention_90d", area: "valor recorrente" },
] as const;
